import { useState } from "react";
import Image from "next/image";
import { Card, CardContent, CardHeader } from "@tracify/ui/card";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { useConnectedAdAccounts } from "@lib/api-hooks/useAdAccounts/useConnectedAdAccounts";
import { useGetPostbackSourceStatus } from "@lib/api-hooks/useAdAccounts/useGetPostbackSourceStatus";
import { useSetPostbackSourceStatus } from "@lib/api-hooks/useAdAccounts/useSetPostbackSourceStatus";
import { useFeatureFlags } from "@lib/hooks/useFeatureFlags";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { toast } from "sonner";
import GooglePostbackControl from "./GooglePostbackControl";
import GooglePostbackEnabledDialog from "./GooglePostbackEnabledDialog";

export function GoogleConversionPostbackCard() {
  const { getFlag } = useFeatureFlags();
  const conversionPostbackEnabled =
    getFlag("conversion_postback_enabled")?.enabled ?? false;
  const { selectedOrganisationData } = useSelectedStores();
  const organisationId = selectedOrganisationData?.id;
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const { data: connectedAdAccounts, isFetching: isFetchingAccounts } =
    useConnectedAdAccounts(
      {
        organisationId: organisationId ?? "",
        includeAll: false,
      },
      {
        enabled: conversionPostbackEnabled && Boolean(organisationId),
      }
    );
  const googleAccounts = connectedAdAccounts?.accounts?.google ?? [];
  const hasGoogleAccount = googleAccounts.length > 0;
  const { data: postbackStatus, isFetching: isFetchingPostbackStatus } =
    useGetPostbackSourceStatus(organisationId, {
      enabled:
        conversionPostbackEnabled &&
        Boolean(organisationId) &&
        hasGoogleAccount,
    });
  const setPostbackSourceStatusMutation = useSetPostbackSourceStatus();

  if (!conversionPostbackEnabled || !organisationId) {
    return null;
  }

  if (!hasGoogleAccount) {
    return null;
  }

  const isActiveForPostback =
    postbackStatus?.activeForPostback.includes("google") ?? false;

  const handleToggle = async (checked: boolean) => {
    try {
      await setPostbackSourceStatusMutation.mutateAsync({
        source: "google",
        organisationId,
        isActiveForPostback: checked,
      });

      if (checked) {
        setSuccessDialogOpen(true);
      } else {
        toast.success("Conversion postback disabled successfully");
      }
    } catch (error) {
      toast.error(
        (error as Error)?.message ??
          `Failed to ${checked ? "enable" : "disable"} conversion postback`
      );
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-muted w-12 h-12 flex items-center justify-center rounded-lg shrink-0">
                <Image
                  src="/static/logos/google-logo.webp"
                  alt="Google Ads"
                  width={28}
                  height={28}
                  className="h-7 w-7 object-contain"
                />
              </div>
              <p className="h4 text-foreground">Conversion Postback</p>
            </div>
            <div className="flex items-center gap-1.5">
              {isActiveForPostback ? (
                <CheckCircleIcon className="text-green-500 w-5 h-5" />
              ) : (
                <div className="h-5 w-5 rounded-full border-foreground border border-dashed" />
              )}
              <span className="text-sm">
                {isActiveForPostback ? "Active" : "Ready"}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <GooglePostbackControl
            checked={isActiveForPostback}
            disabled={
              isFetchingAccounts ||
              isFetchingPostbackStatus ||
              setPostbackSourceStatusMutation.isPending
            }
            onCheckedChange={(checked) => {
              void handleToggle(checked);
            }}
          />
        </CardContent>
      </Card>

      <GooglePostbackEnabledDialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
      />
    </>
  );
}
